import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cognitoAuthService } from '../services/cognitoAuth';
import { apiService } from '../services/api';
import { Subscription } from '../types';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { initializePaddle, Paddle } from '@paddle/paddle-js';

// User type
type User = {
  name?: string;
  email?: string;
  subscriptionStatus?:
    | 'free'
    | 'trial'
    | 'active'
    | 'canceled'
    | 'past_due'
    | 'expired';
  subscriptionEndDate?: string;
  trialEndDate?: string;
};

type PricingPlan = 'monthly' | 'annual';

const SubscriptionExpiredPage: React.FC = () => {
  const history = useHistory();
  const { i18n } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paddle, setPaddle] = useState<Paddle | null>(null);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const isAuth = await cognitoAuthService.isAuthenticated();
        if (!isAuth) {
          history.push('/login');
          return;
        }

        // Get user profile with subscription status
        try {
          const userProfile = await apiService.getUserProfile();
          setUser({
            name: userProfile.name || userProfile.email,
            email: userProfile.email,
            subscriptionStatus: userProfile.subscriptionStatus || 'free',
            subscriptionEndDate: userProfile.subscriptionEndDate,
            trialEndDate: userProfile.trialEndDate,
          });

          // Redirect if subscription is not expired
          if (
            userProfile.subscriptionStatus === 'active' ||
            userProfile.subscriptionStatus === 'trial'
          ) {
            history.push('/subscription');
            return;
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          const attributes = await cognitoAuthService.getUserAttributes();
          if (attributes) {
            setUser({
              name: attributes.name || attributes.email,
              email: attributes.email,
              subscriptionStatus: 'expired',
            });
          }
        }

        // Get subscription details
        try {
          const response = await apiService.getSubscription();
          setSubscription(response);
        } catch (error) {
          console.error('Error fetching subscription:', error);
        }
      } catch (error) {
        setError('Error al cargar información');
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // Initialize Paddle
    const initPaddle = async () => {
      try {
        const paddleInstance = await initializePaddle({
          environment:
            process.env.REACT_APP_PADDLE_ENVIRONMENT === 'production'
              ? 'production'
              : 'sandbox',
          token: process.env.REACT_APP_PADDLE_CLIENT_TOKEN || '',
        });
        setPaddle(paddleInstance as Paddle);
      } catch (error) {
        console.error('Error initializing Paddle:', error);
      }
    };

    initPaddle();
  }, [history]);

  const handleSubscribe = async (plan: PricingPlan) => {
    if (!paddle) {
      setError('Sistema de pago no disponible. Intenta de nuevo.');
      return;
    }

    setSubscribing(true);
    setError(null);

    try {
      // Call backend to create checkout session with plan type
      const response = await apiService.createSubscription(plan);

      // Open Paddle checkout overlay
      paddle.Checkout.open({
        transactionId: response.transactionId,
        settings: { locale: i18n.language },
      });
    } catch (error) {
      setError('Error al iniciar proceso de pago');
      console.error('Error creating subscription:', error);
    } finally {
      setSubscribing(false);
    }
  };

  if (loading) {
    return (
      <div
        className='min-h-screen flex items-center justify-center'
        style={{ background: 'rgb(8 6 20 / 100%)' }}
      >
        <div className='text-center animate-fade-in'>
          <div className='spinner glow mx-auto mb-6'></div>
          <p className='text-base' style={{ color: 'var(--text-secondary)' }}>
            Cargando...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen' style={{ background: 'rgb(8 6 20 / 100%)' }}>
      <Header variant='player' />

      <div className='container-mobile' style={{ paddingBottom: 40 }}>
        {/* Header */}
        <div className='sub-expired-header animate-fade-in'>
          <div className='sub-expired-icon'>
            <svg
              width='24'
              height='24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
              />
            </svg>
          </div>
          <h1 className='sub-expired-title'>Tu suscripción expiró</h1>
          <p className='sub-expired-subtitle'>
            {user?.subscriptionEndDate
              ? `Tu acceso finalizó el ${new Date(user.subscriptionEndDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}`
              : 'Reactiva para seguir escuchando'}
          </p>
        </div>

        {error && (
          <div
            className='auth-error animate-fade-in'
            style={{ marginBottom: 16 }}
          >
            <svg
              width='16'
              height='16'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              viewBox='0 0 24 24'
              style={{ flexShrink: 0 }}
            >
              <circle cx='12' cy='12' r='10' />
              <line x1='12' y1='8' x2='12' y2='12' />
              <line x1='12' y1='16' x2='12.01' y2='16' />
            </svg>
            {error}
          </div>
        )}

        {/* Plan cards */}
        <div className='sub-plans'>
          {/* Annual — featured */}
          <div className='sub-plan-card sub-plan-card--featured'>
            <div className='sub-plan-card__badge'>Mejor valor · Ahorra 33%</div>
            <div className='sub-plan-card__header'>
              <div className='sub-plan-card__name'>Plan Anual</div>
              <div className='sub-plan-card__price'>
                <span className='sub-plan-card__amount'>$24.99</span>
                <span className='sub-plan-card__period'>/año</span>
              </div>
            </div>
            <button
              onClick={() => handleSubscribe('annual')}
              disabled={subscribing || !paddle}
              className='btn btn-secondary btn-block'
            >
              {subscribing ? 'Procesando...' : 'Reactivar anual'}
            </button>
          </div>

          {/* Monthly */}
          <div className='sub-plan-card'>
            <div className='sub-plan-card__header'>
              <div className='sub-plan-card__name'>Plan Mensual</div>
              <div className='sub-plan-card__price'>
                <span className='sub-plan-card__amount'>$2.99</span>
                <span className='sub-plan-card__period'>/mes</span>
              </div>
            </div>
            <button
              onClick={() => handleSubscribe('monthly')}
              disabled={subscribing || !paddle}
              className='btn btn-primary btn-block'
            >
              {subscribing ? 'Procesando...' : 'Reactivar mensual'}
            </button>
          </div>
        </div>

        <p className='sub-trust'>
          ✓ Cancela cuando quieras &nbsp;·&nbsp; ✓ Pago seguro con Paddle
        </p>

        <div style={{ textAlign: 'center', marginTop: 28 }}>
          <button
            onClick={() => history.push('/tracks')}
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.35)',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Volver
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SubscriptionExpiredPage;
